import type { Dayjs } from 'dayjs'

import type { DayOfWeek } from '~/components/forms/DaysOfWeekInput'
import type {
  PricingModel,
  ProductEntityType,
  ProductType,
  ProductVisibility,
  TierQuantitySource,
  UsagePricingType,
  VolumeTiersMode
} from '~/data/types/billing'
import type { TaxBehavior } from '~/app/settings/(billing)/taxes/components/taxOptions'
import type { BillingCurrency } from '~/data/types/currencies'

export const BILLING_TYPES = ['ONE_TIME', 'RECURRING'] as const
export type BillingType = (typeof BILLING_TYPES)[number]

export const BILLING_PERIODS = ['DAY', 'WEEK', 'MONTH', 'QUARTER', 'YEAR'] as const
export type BillingPeriod = (typeof BILLING_PERIODS)[number]

type TimeOfUseRatePeriod = {
  name: string
  daysOfWeek: DayOfWeek[]
  startTime?: Dayjs
  endTime?: Dayjs
  rate: number
}

type IdleFee = {
  rate?: number | null
  gracePeriod?: number | null
  stepSize?: number | null
}

type ReservationConfig = {
  noShowPenalty?: number | null
}

export type TimePeriod = {
  startTime: Dayjs
  endTime: Dayjs
  daysOfWeek: DayOfWeek[]
}

export type RateRestriction = {
  startTime?: Dayjs
  endTime?: Dayjs
  daysOfWeek: DayOfWeek[]
  maxDurationMinutes?: number
  maxSocPercent?: number
}

type TimeOfUseRate = {
  name: string
  description?: string | null
  validFrom: string
  validTo?: string | null
  isYearlyRecurring: boolean
  /**
   * Yearly recurrence window: a Dayjs while being picked, a reference-year YYYY-MM-DD string when
   * hydrated from the API, or an ISO string after the yup Dayjs coercion; sent to the API as --MM-dd.
   */
  recurrenceFrom?: string | Dayjs | null
  recurrenceTo?: string | Dayjs | null
  isActive: boolean
  periods?: TimeOfUseRatePeriod[]
  initiationFee?: number
  hasIdleFee: boolean
  idleFee?: IdleFee
  hasReservations: boolean
  reservationConfig?: ReservationConfig
  restrictions?: RateRestriction[]
  lockRateAtPlugIn?: boolean
}

type VolumeTierFormData = {
  upTo?: number | null
  unitAmount: number
  flatAmount?: number | null
}

type PricingFormData = {
  pricingModel: PricingModel
  usagePricingType?: UsagePricingType
  amount?: number
  currency?: BillingCurrency
  billingType?: BillingType
  billingPeriod?: BillingPeriod
  priceDescription?: string | null
  rates?: TimeOfUseRate[]
  volumeTiers?: VolumeTierFormData[]
  tiersMode?: VolumeTiersMode
  tierQuantitySource?: TierQuantitySource
  /** Per-price tax behavior. Defaults to INCLUSIVE. */
  taxBehavior?: TaxBehavior
}

export type EntitySelectionFormData = {
  entityId: string
  entityType: ProductEntityType
}

export type MetadataEntryFormData = {
  key: string
  value: string
}

export type ProductFormData = {
  name: string
  description?: string
  type: ProductType
  visibility: ProductVisibility
  /** Product-level Stripe tax code (txcd_*). Optional. */
  taxCode?: string
  /**
   * Whether this PUBLIC product sets its own walk-up card pre-authorization hold. Off means the platform default
   * applies; only meaningful (and only rendered) when visibility is PUBLIC.
   */
  authorizationChargeEnabled: boolean
  /** Walk-up pre-authorization hold in major currency units, in the product's pricing currency. */
  authorizationCharge?: number
  metadataEntries: MetadataEntryFormData[]
  pricing: PricingFormData
  entitySelections: EntitySelectionFormData[]
}
